import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiButton from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import React, { FC } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import PrimaryButton from "../../../../components/redesign/elements/Button/PrimaryButton";

export type UtmParams = {
  campaign: string;
  content: string;
  medium: string;
  source: string;
  term: string;
};

type UtmParamsModalRedesignProps = {
  open: boolean;
  initialValues: UtmParams;
  onClose: () => void;
  onSave: (params: UtmParams) => void;
};

const ModalHeader = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "20px 24px",
  borderBottom: `1px solid ${theme.palette.brand.gray100}`,
  background: theme.palette.brand.white,
}));

const ModalTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
  padding: "24px !important",
  minWidth: 440,
  maxWidth: 520,
  display: "flex",
  flexDirection: "column",
  gap: 16,
}));

const FieldGroup = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const FieldLabel = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  color: theme.palette.brand.gray500,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
}));

const RequiredMark = styled("span")(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.coral,
}));

const InputSkin = styled("input")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray100,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  fontSize: 13,
  color: theme.palette.brand.gray900,
  padding: "8px 12px",
  outline: "none",
  width: "100%",
  boxSizing: "border-box" as const,
  "&::placeholder": {
    color: theme.palette.brand.gray500,
  },
  "&:focus": {
    borderColor: theme.palette.brand.blueAccent,
  },
}));

const ErrorText = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  color: theme.palette.brand.errorRed,
}));

const StyledDialogActions = styled(DialogActions)(({ theme }: { theme: Theme }) => ({
  padding: "16px 24px",
  borderTop: `1px solid ${theme.palette.brand.gray100}`,
  background: theme.palette.brand.white,
}));

const CancelButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray700,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  borderColor: theme.palette.brand.gray300,
  borderRadius: 8,
  padding: "8px 14px",
}));

const isAllEmpty = (values: UtmParams): boolean =>
  !values.campaign.trim() &&
  !values.content.trim() &&
  !values.medium.trim() &&
  !values.source.trim() &&
  !values.term.trim();

// All fields empty is valid — that is how applied params get cleared/deleted.
// Otherwise campaign, content, and medium are required.
const validate = (values: UtmParams): Partial<Record<keyof UtmParams, string>> => {
  if (isAllEmpty(values)) return {};
  const errors: Partial<Record<keyof UtmParams, string>> = {};
  if (!values.campaign.trim()) errors.campaign = "Required field.";
  if (!values.content.trim()) errors.content = "Required field.";
  if (!values.medium.trim()) errors.medium = "Required field.";
  return errors;
};

const REQUIRED_FIELDS: { name: keyof UtmParams; label: string }[] = [
  { name: "campaign", label: "Campaign" },
  { name: "content", label: "Content" },
  { name: "medium", label: "Medium" },
];

const OPTIONAL_FIELDS: { name: keyof UtmParams; label: string }[] = [
  { name: "source", label: "Source" },
  { name: "term", label: "Term" },
];

const UtmParamsModalRedesign: FC<UtmParamsModalRedesignProps> = ({ open, initialValues, onClose, onSave }) => {
  const handleSubmit = (values: UtmParams, actions: FormikHelpers<UtmParams>) => {
    onSave({
      campaign: values.campaign.trim(),
      content: values.content.trim(),
      medium: values.medium.trim(),
      source: values.source.trim(),
      term: values.term.trim(),
    });
    actions.setSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" aria-labelledby="utm-parameters-modal">
      <ModalHeader>
        <ModalTitle>UTM Parameters</ModalTitle>
      </ModalHeader>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validate={validate}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ errors, touched }) => (
          <Form>
            <StyledDialogContent>
              {REQUIRED_FIELDS.map(({ name, label }) => (
                <FieldGroup key={name}>
                  <FieldLabel>
                    {label} <RequiredMark>*</RequiredMark>
                  </FieldLabel>
                  <Field as={InputSkin} type="text" name={name} placeholder={`utm_${name}`} />
                  {touched[name] && errors[name] && <ErrorText>{errors[name]}</ErrorText>}
                </FieldGroup>
              ))}

              {OPTIONAL_FIELDS.map(({ name, label }) => (
                <FieldGroup key={name}>
                  <FieldLabel>{label}</FieldLabel>
                  <Field as={InputSkin} type="text" name={name} placeholder={`utm_${name}`} />
                </FieldGroup>
              ))}
            </StyledDialogContent>

            <StyledDialogActions>
              <CancelButton variant="outlined" onClick={onClose}>
                Cancel
              </CancelButton>
              <PrimaryButton type="submit">Apply</PrimaryButton>
            </StyledDialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default UtmParamsModalRedesign;
